import { useEffect, useRef, useState } from "react";
import LogoPerception from '../../assets/images/logos/logo-blue-black.png';
import LogoRenault from '../../assets/images/logos/Renault_Logo.png';
import { useSocket } from "../../contexts/SocketContext";
import { LUBRICATION_TEXTS, STEP_COLORS, PHASES_COLORS, PHASES_TEXTS } from "../../services/live-interface/renault-interface-service";
import ToggleSwitch from "../../components/toggle/ToggleSwitch";
import audioFileLubrication from '../../assets/sounds/lubrication_error.mp3';
import audioFileSuccess from '../../assets/sounds/success.mp3';
import audioFileClips0 from '../../assets/sounds/clips_0.mp3';
import audioFileClips1 from '../../assets/sounds/clips_1.mp3';
import audioFileClips2 from '../../assets/sounds/clips_2.mp3';
import audioFileClips3 from '../../assets/sounds/clips_3.mp3';
import audioFileClips4 from '../../assets/sounds/clips_4.mp3';
import audioFileClips5 from '../../assets/sounds/clips_5.mp3';
import audioFileClips6 from '../../assets/sounds/clips_6.mp3';
import audioFileClips7 from '../../assets/sounds/clips_7.mp3';
import audioFilePipes0 from '../../assets/sounds/pipes_0.mp3';
import audioFilePipes1 from '../../assets/sounds/pipes_1.mp3';
import audioFilePipes2 from '../../assets/sounds/pipes_2.mp3';
import audioFilePipes3 from '../../assets/sounds/pipes_3.mp3';
import audioFilePipes4 from '../../assets/sounds/pipes_4.mp3';
import audioFilePipes5 from '../../assets/sounds/pipes_5.mp3';
import audioFilePipes6 from '../../assets/sounds/pipes_6.mp3';
import audioFilePipes7 from '../../assets/sounds/pipes_7.mp3';
import audioStartAnalysis from '../../assets/sounds/start_analysis.mp3';
import audioStopAnalysis from '../../assets/sounds/stop_analysis.mp3';
import audioNoAnalysis from '../../assets/sounds/no_analysis.mp3';
import audioLubricationSuccess from '../../assets/sounds/lubrication_success.mp3';
import audioResults1 from '../../assets/sounds/results_1.mp3';
import audioResults2 from '../../assets/sounds/results_2.mp3';
import audioResults3 from '../../assets/sounds/results_3.mp3';
import { ArrowPathIcon } from "@heroicons/react/20/solid";
import { formatSecondsToMinutesSeconds } from "../../helpers/date/date-helper";
import '../../styles/renault/renault.scss';

const RenaultTimeline = ({ phases }) => {
  const startDate = phases.length > 0 ? phases[0].start_date : null;
  const liveDuration = phases.length > 0 ? phases[phases.length - 1].end_date - phases[0].start_date : 0;
  const displayDuration = Math.max(60, liveDuration);

  const ticksNumber = Math.min(Math.ceil(displayDuration / 30), 10);
  const tickInterval = displayDuration / ticksNumber;

  return (
    <div className="h-full flex">
      <div className="h-full w-full flex flex-col gap-1">
        {/* Live timeline */}
        <div className="bg-perception-black-700 flex-1 relative rounded-md">
          {
            phases.filter((phase) => phase.type > 0).map((phase, index) => {
              if (phase.type <= 0) return null;

              const barPosition = (phase.start_date - startDate) / displayDuration * 100;
              const barWidth = (phase.end_date - phase.start_date) / displayDuration * 100;
              const barColor = PHASES_COLORS[phase.type];
              const barText = PHASES_TEXTS[phase.type];

              return (
                <div className="h-full absolute py-1 z-5" style={{ left: `${barPosition}%`, width: `${barWidth}%` }} key={index}>
                  <div className={`h-full ${barColor} truncate select-none rounded-md flex items-center p-2 text-sm text-white`}>
                    {barText}
                  </div>
                </div>
              );
            })
          }
        </div>

        {/* Ticks */}
        <div className="relative flex-shrink-0">
          {Array.from({ length: ticksNumber + 1 }).map((_, tickIndex) => {
            const tickTime = tickIndex * tickInterval;
            const tickPosition = (tickTime / displayDuration) * 100;
            const halfTickPosition = (tickTime + tickInterval / 2) / displayDuration * 100;

            return (
              <div key={tickIndex} className='inline-block select-none' style={{ width: `${100 / ticksNumber}%` }}>
                {/* TICK */}
                <div className="absolute border-l border-white" style={{ height: "10px", left: `${tickPosition}%`, top: "5px" }}/>

                {/* HALF TICK */}
                {halfTickPosition <= 100 && (
                  <div className="absolute border-l border-white" style={{height: "5px", left: `${halfTickPosition}%`, top: "5px"}}/>
                )}

                {/* TIME */}
                <div className='text-xs text-white' style={{ position: 'absolute', left: `${tickPosition}%`, top: '20px', transform: 'translateX(-50%)' }}>
                  {formatSecondsToMinutesSeconds(tickTime)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const RenaultTimelineZone = ({ currentData, phases, stopMessage, past }) => {
  const bgColor = past ? 'bg-perception-black-800/60' : 'bg-perception-black-800';
  const analysisTextColor = stopMessage === null ? 'text-perception-blue' : 'text-perception-black-500';

  return (
    <div className={`${bgColor} h-full max-h-[400px] flex w-full p-4 gap-8 rounded-md`}>

      {/* Timeline part */}
      <div className="flex-1 flex flex-col justify-center">
        <div className="flex-shrink-0 flex items-center pl-4 mb-4 text-2xl text-white gap-4">
          <div>
            { past ? 'Moteur précédent' : 'Moteur sur le pas' }
          </div>
          <div className={`bg-perception-gray-100 rounded-md px-4 py-2 ${analysisTextColor}`}>
            {
              stopMessage !== null ?
              <>
                {stopMessage}
              </>
              :
              <div className="flex gap-4 items-center">
                <style>{`
                  @keyframes bounceScale {
                    0%, 100% {
                      transform: scale(1);
                    }
                    50% {
                      transform: scale(1.15);
                    }
                  }
                `}</style>
                <ArrowPathIcon className="animate-spin w-6 h-6 text-perception-blue"/> Analyse en cours
              </div>
            }
          </div>
        </div>
        <div className="flex-1 pl-4">
          <RenaultTimeline phases={phases}/>
        </div>
      </div>

      {/* Data part */}
      <div className="flex-shrink-0 flex flex-col justify-center px-4 gap-8 text-white max-w-[600px]">
        {/* Lubrication */}
        <div className="flex flex-col gap-1 items-center text-xl">
          <div>Lubrification</div>
            <div className={`w-4/5 h-12 flex justify-center items-center rounded-md overflow-auto text-center text-3xl
              ${STEP_COLORS[currentData?.lubrication_states]}`
            }>
              {LUBRICATION_TEXTS[currentData?.lubrication_states]}
          </div>
        </div>

        {/* Emmanchements */}
        {/*<div className="flex flex-col gap-1 items-center text-2xl">*/}
        {/*  <div>Emmanchements</div>*/}
        {/*  <div className="flex gap-4 w-full">*/}
        {/*    {Array.from({ length: 7 }).map((_, index) => (*/}
        {/*      <div key={index} className={`w-full px-3 py-2 rounded-md flex items-center justify-center*/}
        {/*        ${STEP_COLORS[currentData?.pipes_states?.[index]]}*/}
        {/*      `}>*/}
        {/*        #{index + 1}*/}
        {/*      </div>*/}
        {/*    ))}*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/* Clipsage block */}
        {/*<div className="flex flex-col gap-1 items-center text-2xl">*/}
        {/*  <div>Clipsages</div>*/}
        {/*  <div className="flex gap-4 w-full">*/}
        {/*    {Array.from({ length: 7 }).map((_, index) => (*/}
        {/*      <div key={index} className={`w-full px-3 py-2 rounded-md flex items-center justify-center*/}
        {/*        ${STEP_COLORS[currentData?.clips_states?.[index]]}*/}
        {/*      `}>*/}
        {/*        #{index + 1}*/}
        {/*      </div>*/}
        {/*    ))}*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/* Block niveau de confiance */}
        <div className="flex gap-4">
          <div className="w-1/3 flex flex-col gap-1 justify-around items-center text-center text-lg bg-perception-gray-800 rounded-md p-4">
            <div>Emmanchements</div>
            <div className="text-2xl">{currentData?.analysis_results?.[0] || '-'}</div>
          </div>

          <div className="w-1/3 flex flex-col gap-1 justify-around items-center text-center text-lg bg-perception-gray-800 rounded-md p-4">
            <div>Clipsages</div>
            <div className="text-2xl">{currentData?.analysis_results?.[1] || '-'}</div>
          </div>

          <div className="w-1/3 flex flex-col gap-1 justify-around items-center text-center text-lg bg-perception-gray-800 rounded-md p-4">
            <div>Niveau de confiance</div>
            <div className="text-2xl">{currentData?.analysis_results?.[2] || '-'}</div>
          </div>
        </div>

      </div>
    </div>
  );
};

const RenaultProgression = ({ engines }) => {
  const PERCENT_START = 18.7;
  const PERCENT_STOP = 94.4;

  const enginesPositionsPercent = engines.filter((engine) => engine !== null).map((engine) => engine * 100);

  return (
    <div className="w-full flex-shrink-0 py-1 flex justify-center items-center">
      <div className="w-full bg-perception-gray-700 h-10 rounded-full relative">
        <div className="w-full h-full absolute top-0 left-0 rounded-full overflow-hidden">
          <div className="h-full bg-scanline border-r-4 border-white" style={{ width: PERCENT_START + '%' }}/>
          <div className="h-full absolute top-0 right-0 bg-scanline border-l-4 border-white" style={{ width: (100 - PERCENT_STOP) + '%' }}/>
        </div>
        {
          enginesPositionsPercent.map((position, index) => {
            const color = position >= PERCENT_START && position < PERCENT_STOP ? 'bg-perception-blue' : 'bg-perception-gray-800';
            return (
              <div key={index} style={{ left: position + '%'}} className={`${color} top-1/2 text-xs absolute w-16 h-16 rounded-full flex justify-center items-center text-white -translate-x-1/2 -translate-y-1/2`}>
                Moteur
              </div>
            )
          })
        }
      </div>
    </div>
  );
};

export const RenaultLivePage = () => {

  const { socket, subscribeToEvent, unsubscribeFromEvent } = useSocket();

  const [currentData, setCurrentData] = useState([{}, {}]);
  const [phases, setPhases] = useState([[], []]);
  const [progressions, setProgressions] = useState([]);
  const [stopMessages, setStopMessages] = useState([null, null]);

  const lockedData = useRef(false);

  const [soundEnabled, setSoundEnabled] = useState(false);

  useEffect(() => {
    if (!socket) return;

    const handleReceivedData = (data) => {
      if (lockedData.current) return;
      // console.log(data.lubrication_states);

      // Data management
      setCurrentData(prevData => {
        const newData = [prevData[0], prevData[1]];

        for (const [key, value] of Object.entries(data)) {
          if (Array.isArray(value) && value.length === 2) {
            newData[0][key] = value[0];
            newData[1][key] = value[1];
          }
        }
        return newData;
      });

      // Phases management
      if (data?.analysis_states !== null && data?.analysis_states !== undefined) {
        handleReceivedCurrentPhase(data.analysis_states);
      }

      // Progressions management
      if (data?.engines_progressions !== null && data?.engines_progressions !== undefined) {
        setProgressions(data.engines_progressions);
      }

      // Stop message management
      if (data?.stop_causes !== null && data?.stop_causes !== undefined) {
        setStopMessages(data.stop_causes);
      }

      // Results management
      // if (data?.analysis_results !== null && data?.analysis_results !== undefined) {
      //
      // }

      // Switch management
      if (data?.switch !== null && data?.switch !== undefined && data?.switch === true) {
        setCurrentData(currentData => [{}, currentData[0]]);
        setPhases(phases => [[], phases[0]]);
        setStopMessages(stopMessages => [null, stopMessages[0]])
      }

      // Sound management
      if (soundEnabled) {
        if (data?.play_lubrication_error === 1) {
          emitSoundLubrication();
        }
        else if (data?.play_pipes_error !== undefined && data?.play_pipes_error !== 0) {
          emitSoundPipes(data?.play_pipes_error);
        }
        else if (data?.play_clips_error !== undefined && data?.play_clips_error !== 0) {
          emitSoundClips(data?.play_clips_error);
        }
        else if (data?.play_engine_start !== undefined && data?.play_engine_start !== 0) {
          emitSoundStartAnalysis();
        }
        else if (data?.play_engine_end !== undefined && data?.play_engine_end !== 0) {
          emitSoundStopAnalysis();
        }
        else if (data?.play_analysis_stop !== undefined && data?.play_analysis_stop !== 0) {
          emitSoundNoAnalysis();
        }
        else if (data?.play_success === 1) {
          emitSoundSuccess();
        }
        else if (data?.play_confidence !== undefined && data?.play_confidence !== 0) {
          emitSoundResults(data?.play_confidence);
        }
      }
    };

    const handleReceivedCurrentPhase = (currentPhaseType) => {
      // Detect process end
      const eventDate = new Date().getTime() / 1000;

      setPhases(prevPhases => {
        const newPhases = [...prevPhases];

        [0, 1].map((engineIndex) => {
          const firstPhases = [...newPhases[engineIndex]];
          const lastPhase = firstPhases.length > 0 ? firstPhases[firstPhases.length - 1] : null;

          // First detected phase or new phase
          if (lastPhase === null || lastPhase.type !== currentPhaseType[engineIndex]) {
            newPhases[engineIndex] = [...firstPhases, {type: currentPhaseType[engineIndex], start_date: eventDate, end_date: eventDate}];
          }
          // Continuing the same phase
          else {
            newPhases[engineIndex] = firstPhases.map((phase, index) =>
              index === firstPhases.length - 1 ? { ...phase, end_date: eventDate } : phase
            );
          }

        });

        return newPhases;
      });
    };

    subscribeToEvent('current_data', handleReceivedData);

    return () => {
      unsubscribeFromEvent('current_data', handleReceivedData);
    };
  }, [socket, soundEnabled]);

  const soundLubricationRef = useRef(null);
  const emitSoundLubrication = () => { if (soundLubricationRef.current) soundLubricationRef.current.play(); };

  const soundPipes0Ref = useRef(null);
  const soundPipes1Ref = useRef(null);
  const soundPipes2Ref = useRef(null);
  const soundPipes3Ref = useRef(null);
  const soundPipes4Ref = useRef(null);
  const soundPipes5Ref = useRef(null);
  const soundPipes6Ref = useRef(null);
  const soundPipes7Ref = useRef(null);

  const soundClips0Ref = useRef(null);
  const soundClips1Ref = useRef(null);
  const soundClips2Ref = useRef(null);
  const soundClips3Ref = useRef(null);
  const soundClips4Ref = useRef(null);
  const soundClips5Ref = useRef(null);
  const soundClips6Ref = useRef(null);
  const soundClips7Ref = useRef(null);

  const soundStartAnalysis = useRef(null);
  const emitSoundStartAnalysis = () => { if (soundStartAnalysis.current) soundStartAnalysis.current.play(); };
  const soundStopAnalysis = useRef(null);
  const emitSoundStopAnalysis = () => { if (soundStopAnalysis.current) soundStopAnalysis.current.play(); };
  const soundNoAnalysis = useRef(null);
  const emitSoundNoAnalysis = () => { if (soundNoAnalysis.current) soundNoAnalysis.current.play(); };

  const soundResults1Ref = useRef(null);
  const soundResults2Ref = useRef(null);
  const soundResults3Ref = useRef(null);

  const emitSoundResults = (number) => {
    if (number === 1 && soundResults1Ref.current) soundResults1Ref.current.play();
    if (number === 2 && soundResults2Ref.current) soundResults2Ref.current.play();
    if (number === 3 && soundResults3Ref.current) soundResults3Ref.current.play();
  };

  const soundSuccessRef = useRef(null);
  const emitSoundSuccess = () => { if (soundSuccessRef.current) soundSuccessRef.current.play(); };

  const emitSoundPipes = (number) => {
    if (number === -1 && soundPipes0Ref.current) soundPipes0Ref.current.play();
    if (number === 1 && soundPipes1Ref.current) soundPipes1Ref.current.play();
    if (number === 2 && soundPipes2Ref.current) soundPipes2Ref.current.play();
    if (number === 3 && soundPipes3Ref.current) soundPipes3Ref.current.play();
    if (number === 4 && soundPipes4Ref.current) soundPipes4Ref.current.play();
    if (number === 5 && soundPipes5Ref.current) soundPipes5Ref.current.play();
    if (number === 6 && soundPipes6Ref.current) soundPipes6Ref.current.play();
    if (number === 7 && soundPipes7Ref.current) soundPipes7Ref.current.play();
  };

  const emitSoundClips = (number) => {
    if (number === -1 && soundClips0Ref.current) soundClips0Ref.current.play();
    if (number === 1 && soundClips1Ref.current) soundClips1Ref.current.play();
    if (number === 2 && soundClips2Ref.current) soundClips2Ref.current.play();
    if (number === 3 && soundClips3Ref.current) soundClips3Ref.current.play();
    if (number === 4 && soundClips4Ref.current) soundClips4Ref.current.play();
    if (number === 5 && soundClips5Ref.current) soundClips5Ref.current.play();
    if (number === 6 && soundClips6Ref.current) soundClips6Ref.current.play();
    if (number === 7 && soundClips7Ref.current) soundClips7Ref.current.play();
  };

  return (
    <div className='h-full overflow-hidden bg-gray-100 flex flex-col'>

      <div className="h-full flex-1 flex flex-col justify-center items-center gap-8 p-8">
        <RenaultTimelineZone currentData={currentData[0]} phases={phases[0]} stopMessage={stopMessages[0]}/>
        <RenaultTimelineZone past={true} currentData={currentData[1]} phases={phases[1]} stopMessage={stopMessages[1]}/>
        <RenaultProgression engines={progressions}/>
      </div>


      <div className="h-12 px-4 flex justify-between items-center text-xl gap-4 text-black">
        {/* Logos */}
        <div className="flex justify-center items-center text-xl gap-4 text-black">
          <img src={LogoPerception} alt="logo-perception" className="max-w-[200px] max-h-10" />
          x
          <img src={LogoRenault} alt="logo-LCA" className="max-w-[200px] max-h-10" />
        </div>

        {/* Sound management */}
        <div className="text-lg flex items-center gap-2 font-semibold">
          Avertisseur sonore
          <ToggleSwitch enabled={soundEnabled} setEnabled={setSoundEnabled} />
          <audio ref={soundLubricationRef} src={audioFileLubrication} />

          <audio ref={soundClips0Ref} src={audioFileClips0} />
          <audio ref={soundClips1Ref} src={audioFileClips1} />
          <audio ref={soundClips2Ref} src={audioFileClips2} />
          <audio ref={soundClips3Ref} src={audioFileClips3} />
          <audio ref={soundClips4Ref} src={audioFileClips4} />
          <audio ref={soundClips5Ref} src={audioFileClips5} />
          <audio ref={soundClips6Ref} src={audioFileClips6} />
          <audio ref={soundClips7Ref} src={audioFileClips7} />

          <audio ref={soundPipes0Ref} src={audioFilePipes0} />
          <audio ref={soundPipes1Ref} src={audioFilePipes1} />
          <audio ref={soundPipes2Ref} src={audioFilePipes2} />
          <audio ref={soundPipes3Ref} src={audioFilePipes3} />
          <audio ref={soundPipes4Ref} src={audioFilePipes4} />
          <audio ref={soundPipes5Ref} src={audioFilePipes5} />
          <audio ref={soundPipes6Ref} src={audioFilePipes6} />
          <audio ref={soundPipes7Ref} src={audioFilePipes7} />

          <audio ref={soundStartAnalysis} src={audioStartAnalysis} />
          <audio ref={soundStopAnalysis} src={audioStopAnalysis} />
          <audio ref={soundNoAnalysis} src={audioNoAnalysis} />

          <audio ref={soundResults1Ref} src={audioResults1} />
          <audio ref={soundResults2Ref} src={audioResults2} />
          <audio ref={soundResults3Ref} src={audioResults3} />

          <audio ref={soundSuccessRef} src={audioLubricationSuccess} />
        </div>
      </div>
    </div>
  );
};
